import { useMemo } from 'react';
import { CSVFileEntry, ZeitschritteData, TimeInformationDatabaseData, CategoryDataItem, FeiertageDataItem } from '../../../types/common';

export interface ValidationError {
  component: 'zeitschritte' | 'inputFiles' | 'outputFiles' | 'fileConfig';
  field?: string;
  message: string;
}

export interface FileValidationStatus {
  totalFiles: number;
  configuredFiles: number;
  invalidFiles: Array<{
    id: string;
    name: string;
    missingFields: string[];
  }>;
}

export interface ZeitschritteStatus {
  isComplete: boolean;
  hasEingabe: boolean;
  hasAusgabe: boolean;
  hasZeitschrittweite: boolean;
  hasOffset: boolean;
  isSaved: boolean;
}

export interface DuplicateBezeichnungStatus {
  hasDuplicates: boolean;
  duplicateNames: string[];
}

export interface TimeInfoFieldStatus {
  fieldName: string;
  isValid: boolean;
  missingFields: string[];
}

export interface TimeInfoValidationStatus {
  isComplete: boolean;
  invalidFields: TimeInfoFieldStatus[];
  isSaved: boolean;
}

export interface DatasetValidationResult {
  isValid: boolean;
  errors: ValidationError[];

  // Detailed status for checklist
  zeitschritteStatus: ZeitschritteStatus;
  inputFilesStatus: FileValidationStatus;
  outputFilesStatus: FileValidationStatus;
  duplicateBezeichnungStatus: DuplicateBezeichnungStatus;
  timeInfoStatus: TimeInfoValidationStatus;

  // Legacy properties
  hasInputFiles: boolean;
  hasOutputFiles: boolean;
  hasZeitschritte: boolean;
  invalidFileIds: string[];
}

/**
 * Validate a single TimeInfo field (jahr, monat, etc.)
 */
function validateTimeInfoField(
  fieldName: string,
  isChecked: boolean,
  data: CategoryDataItem | FeiertageDataItem | undefined,
  isFeiertag: boolean = false
): TimeInfoFieldStatus {
  const missingFields: string[] = [];

  // Only validate if checkbox is checked
  if (!isChecked) {
    return { fieldName, isValid: true, missingFields: [] };
  }

  // Datenform is required
  if (!data?.datenform) {
    missingFields.push('datenform');
  }

  // Zeithorizont Start/End required when datenform is "Zeithorizont"
  if (data?.datenform === 'Zeithorizont') {
    if (!data.zeithorizontStart || data.zeithorizontStart.trim() === '') {
      missingFields.push('zeithorizontStart');
    } else {
      const startNum = parseFloat(data.zeithorizontStart);
      if (isNaN(startNum)) {
        missingFields.push('zeithorizontStartInvalid');
      }
    }

    if (!data.zeithorizontEnd || data.zeithorizontEnd.trim() === '') {
      missingFields.push('zeithorizontEnd');
    } else {
      const endNum = parseFloat(data.zeithorizontEnd);
      if (isNaN(endNum)) {
        missingFields.push('zeithorizontEndInvalid');
      }
    }

    // Check start <= end
    if (data.zeithorizontStart && data.zeithorizontEnd) {
      const startNum = parseFloat(data.zeithorizontStart);
      const endNum = parseFloat(data.zeithorizontEnd);
      if (!isNaN(startNum) && !isNaN(endNum) && startNum > endNum) {
        missingFields.push('zeithorizontStartGreaterThanEnd');
      }
    }
  }

  // Skalierung Min/Max required when skalierung is "ja"
  if (data?.skalierung === 'ja') {
    if (!data.skalierungMin || data.skalierungMin.trim() === '') {
      missingFields.push('skalierungMin');
    } else {
      const minNum = parseFloat(data.skalierungMin);
      if (isNaN(minNum)) {
        missingFields.push('skalierungMinInvalid');
      }
    }

    if (!data.skalierungMax || data.skalierungMax.trim() === '') {
      missingFields.push('skalierungMax');
    } else {
      const maxNum = parseFloat(data.skalierungMax);
      if (isNaN(maxNum)) {
        missingFields.push('skalierungMaxInvalid');
      }
    }

    // Check min <= max
    if (data.skalierungMin && data.skalierungMax) {
      const minNum = parseFloat(data.skalierungMin);
      const maxNum = parseFloat(data.skalierungMax);
      if (!isNaN(minNum) && !isNaN(maxNum) && minNum > maxNum) {
        missingFields.push('skalierungMinMax');
      }
    }
  }

  // Land required for feiertag
  if (isFeiertag && isChecked) {
    const feiertageData = data as FeiertageDataItem;
    if (!feiertageData?.land || feiertageData.land.trim() === '') {
      missingFields.push('land');
    }
  }

  return {
    fieldName,
    isValid: missingFields.length === 0,
    missingFields
  };
}

/**
 * Validate TimeInformation data
 */
function validateTimeInformation(
  timeInfo: TimeInformationDatabaseData | undefined,
  timeInfoIsDirty: boolean = false
): TimeInfoValidationStatus {
  const isSaved = !timeInfoIsDirty;

  if (!timeInfo) {
    return { isComplete: true, invalidFields: [], isSaved };
  }

  const invalidFields: TimeInfoFieldStatus[] = [];

  // Check each field
  const fields: Array<{ name: string; checked: boolean; data: CategoryDataItem | FeiertageDataItem | undefined; isFeiertag?: boolean }> = [
    { name: 'Jahr', checked: timeInfo.jahr, data: timeInfo.category_data?.jahr },
    { name: 'Monat', checked: timeInfo.monat, data: timeInfo.category_data?.monat },
    { name: 'Woche', checked: timeInfo.woche, data: timeInfo.category_data?.woche },
    { name: 'Tag', checked: timeInfo.tag, data: timeInfo.category_data?.tag },
    { name: 'Feiertag', checked: timeInfo.feiertag, data: timeInfo.category_data?.feiertag, isFeiertag: true }
  ];

  for (const field of fields) {
    const status = validateTimeInfoField(field.name, field.checked, field.data, field.isFeiertag);
    if (!status.isValid) {
      invalidFields.push(status);
    }
  }

  return {
    isComplete: invalidFields.length === 0 && isSaved,
    invalidFields,
    isSaved
  };
}

/**
 * Get missing fields for a CSV file entry
 */
function getMissingFields(entry: CSVFileEntry): string[] {
  const missing: string[] = [];

  // Only validate files that are uploaded (have storagePath)
  if (!entry.storagePath) return missing;

  // Datenform is required
  if (!entry.datenform) {
    missing.push('datenform');
  }

  // Zeithorizont required when datenform is "Historische Daten"
  if (entry.datenform === 'Historische Daten') {
    if (!entry.zeithorizontStart) {
      missing.push('zeithorizontStart');
    }
    if (!entry.zeithorizontEnd) {
      missing.push('zeithorizontEnd');
    }
  }

  // Datenanpassung is required
  if (!entry.datenanpassung) {
    missing.push('datenanpassung');
  }

  // Skalierung min/max required when skalierung is 'ja'
  if (entry.skalierung === 'ja') {
    if (!entry.skalierungMin) {
      missing.push('skalierungMin');
    }
    if (!entry.skalierungMax) {
      missing.push('skalierungMax');
    }
    // Check min <= max
    if (entry.skalierungMin && entry.skalierungMax) {
      const minVal = parseFloat(entry.skalierungMin);
      const maxVal = parseFloat(entry.skalierungMax);
      if (!isNaN(minVal) && !isNaN(maxVal) && minVal > maxVal) {
        missing.push('skalierungMinMax');
      }
    }
  }

  return missing;
}

/**
 * Validates if a CSV file entry has all required configuration
 */
function validateFileEntry(entry: CSVFileEntry): ValidationError[] {
  const errors: ValidationError[] = [];
  const componentType = entry.type === 'output' ? 'outputFiles' : 'inputFiles';
  const missingFields = getMissingFields(entry);

  missingFields.forEach(field => {
    let message = `${entry.bezeichnung || entry.fileName}: `;
    switch (field) {
      case 'datenform':
        message += 'Datenform is required';
        break;
      case 'zeithorizontStart':
        message += 'Zeithorizont Start is required';
        break;
      case 'zeithorizontEnd':
        message += 'Zeithorizont End is required';
        break;
      case 'datenanpassung':
        message += 'Datenanpassung is required';
        break;
      case 'skalierungMin':
        message += 'Skalierung Min is required';
        break;
      case 'skalierungMax':
        message += 'Skalierung Max is required';
        break;
      case 'skalierungMinMax':
        message += 'Min cannot be greater than Max';
        break;
      default:
        message += `${field} is required`;
    }
    errors.push({ component: componentType, field, message });
  });

  return errors;
}

/**
 * Get validation status for a list of files
 */
function getFileValidationStatus(files: CSVFileEntry[]): FileValidationStatus {
  const uploadedFiles = files.filter(f => f.storagePath);
  const invalidFiles: FileValidationStatus['invalidFiles'] = [];

  uploadedFiles.forEach(file => {
    const missingFields = getMissingFields(file);
    if (missingFields.length > 0) {
      invalidFiles.push({
        id: file.id,
        name: file.bezeichnung || file.fileName,
        missingFields
      });
    }
  });

  return {
    totalFiles: uploadedFiles.length,
    configuredFiles: uploadedFiles.length - invalidFiles.length,
    invalidFiles
  };
}

/**
 * Hook to validate all required data before generating datasets
 */
export function useDatasetValidation(
  zeitschritte: ZeitschritteData | undefined,
  inputFiles: CSVFileEntry[] | undefined,
  outputFiles: CSVFileEntry[] | undefined,
  timeInfo?: TimeInformationDatabaseData,
  zeitschritteIsDirty: boolean = false,
  timeInfoIsDirty: boolean = false
): DatasetValidationResult {
  return useMemo(() => {
    const errors: ValidationError[] = [];
    const invalidFileIds: string[] = [];

    // 1. Validate Zeitschritte (AllgemeineDaten)
    const hasEingabe = !!(zeitschritte?.eingabe && zeitschritte.eingabe.trim() !== '');
    const hasAusgabe = !!(zeitschritte?.ausgabe && zeitschritte.ausgabe.trim() !== '');
    const hasZeitschrittweite = !!(zeitschritte?.zeitschrittweite && zeitschritte.zeitschrittweite.trim() !== '');
    const hasOffset = !!(zeitschritte?.offset && zeitschritte.offset.trim() !== '');
    const isSaved = !zeitschritteIsDirty;

    const zeitschritteStatus: ZeitschritteStatus = {
      isComplete: hasEingabe && hasAusgabe && hasZeitschrittweite && hasOffset && isSaved,
      hasEingabe,
      hasAusgabe,
      hasZeitschrittweite,
      hasOffset,
      isSaved
    };

    if (!hasEingabe) {
      errors.push({
        component: 'zeitschritte',
        field: 'eingabe',
        message: 'Eingabe (Input timesteps) is required'
      });
    }
    if (!hasAusgabe) {
      errors.push({
        component: 'zeitschritte',
        field: 'ausgabe',
        message: 'Ausgabe (Output timesteps) is required'
      });
    }
    if (!hasZeitschrittweite) {
      errors.push({
        component: 'zeitschritte',
        field: 'zeitschrittweite',
        message: 'Zeitschrittweite (Timestep width) is required'
      });
    }
    if (!hasOffset) {
      errors.push({
        component: 'zeitschritte',
        field: 'offset',
        message: 'Offset is required'
      });
    }
    if (!isSaved) {
      errors.push({
        component: 'zeitschritte',
        field: 'unsaved',
        message: 'Zeitschritte data has unsaved changes'
      });
    }

    // 2. Validate Input Files
    const inputFilesStatus = getFileValidationStatus(inputFiles || []);
    const hasInputFiles = inputFilesStatus.totalFiles > 0;

    if (!hasInputFiles) {
      errors.push({
        component: 'inputFiles',
        message: 'At least one input file is required'
      });
    }

    // Validate each input file's configuration
    (inputFiles || []).filter(f => f.storagePath).forEach(file => {
      const fileErrors = validateFileEntry(file);
      if (fileErrors.length > 0) {
        invalidFileIds.push(file.id);
        errors.push(...fileErrors);
      }
    });

    // 3. Validate Output Files
    const outputFilesStatus = getFileValidationStatus(outputFiles || []);
    const hasOutputFiles = outputFilesStatus.totalFiles > 0;

    if (!hasOutputFiles) {
      errors.push({
        component: 'outputFiles',
        message: 'At least one output file is required'
      });
    }

    // Validate each output file's configuration
    (outputFiles || []).filter(f => f.storagePath).forEach(file => {
      const fileErrors = validateFileEntry(file);
      if (fileErrors.length > 0) {
        invalidFileIds.push(file.id);
        errors.push(...fileErrors);
      }
    });

    // 4. Check for duplicate bezeichnung across all files
    const allFiles = [...(inputFiles || []), ...(outputFiles || [])].filter(f => f.storagePath);
    const bezeichnungCounts = new Map<string, number>();
    allFiles.forEach(file => {
      const name = file.bezeichnung?.trim().toLowerCase();
      if (name) {
        bezeichnungCounts.set(name, (bezeichnungCounts.get(name) || 0) + 1);
      }
    });
    
    const duplicateNames: string[] = [];
    bezeichnungCounts.forEach((count, name) => {
      if (count > 1) {
        // Find the original casing from the first occurrence
        const originalFile = allFiles.find(f => f.bezeichnung?.trim().toLowerCase() === name);
        duplicateNames.push(originalFile?.bezeichnung || name);
      }
    });

    const duplicateBezeichnungStatus: DuplicateBezeichnungStatus = {
      hasDuplicates: duplicateNames.length > 0,
      duplicateNames
    };

    if (duplicateNames.length > 0) {
      errors.push({
        component: 'fileConfig',
        field: 'bezeichnung',
        message: `Duplicate designations found: ${duplicateNames.join(', ')}`
      });
    }

    // 5. Validate TimeInformation
    const timeInfoStatus = validateTimeInformation(timeInfo, timeInfoIsDirty);

    return {
      isValid: errors.length === 0 && timeInfoStatus.isComplete,
      errors,

      // Detailed status for checklist
      zeitschritteStatus,
      inputFilesStatus,
      outputFilesStatus,
      duplicateBezeichnungStatus,
      timeInfoStatus,

      // Legacy properties
      hasInputFiles,
      hasOutputFiles,
      hasZeitschritte: zeitschritteStatus.isComplete,
      invalidFileIds
    };
  }, [zeitschritte, inputFiles, outputFiles, timeInfo, zeitschritteIsDirty, timeInfoIsDirty]);
}

export default useDatasetValidation;
